import React, { useEffect, useId, useRef, useState } from 'react';

import ImageWrapper from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { RecipeResponse } from 'lib/services/models/content-response';

import styles from './Badge.module.scss';
import { BadgeAwareSiteSettings, extractBadgeData } from './Badge.utils';

export interface BadgeProps {
  recipe?: RecipeResponse;
  siteName?: string;
  siteSettings?: BadgeAwareSiteSettings | null;
}

const normalizeBadgeText = (value?: string | null) => value?.trim() || undefined;

const normalizeBadgeUnknownText = (value: unknown) =>
  typeof value === 'string' ? normalizeBadgeText(value) : undefined;

const uniqueBadgeText = (values: Array<string | undefined>) => {
  const seen = new Set<string>();

  return values.filter((value): value is string => {
    if (!value || seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
};

export function Badge({ recipe, siteName, siteSettings }: BadgeProps) {
  const badgeData = extractBadgeData(recipe, siteSettings, siteName);
  const hasBadgeData = Boolean(badgeData);
  const accessibilityId = useId();
  const memberNameRef = useRef<HTMLParagraphElement | null>(null);
  const memberLocationRef = useRef<HTMLParagraphElement | null>(null);
  const [mobileHeightState, setMobileHeightState] = useState<'base' | 'double' | 'triple'>('base');

  const {
    badgeLabel,
    badgeLinkURL,
    badgeLogo,
    badgeRibbon,
    badgeText,
    contestName,
    contestantFullName,
    contestantLocation,
    showMemberInfo,
  } = badgeData ?? { showMemberInfo: false };

  useEffect(() => {
    if (!hasBadgeData || typeof window === 'undefined') {
      setMobileHeightState('base');
      return undefined;
    }

    const countLines = (element: HTMLElement | null) => {
      if (!element) {
        return 0;
      }

      const computedStyle = window.getComputedStyle(element);
      const lineHeight = Number.parseFloat(computedStyle.lineHeight);
      if (!lineHeight || Number.isNaN(lineHeight)) {
        return 1;
      }

      return Math.max(1, Math.round(element.scrollHeight / lineHeight));
    };

    const updateHeightState = () => {
      const maxMemberLines = Math.max(
        countLines(memberNameRef.current),
        countLines(memberLocationRef.current)
      );

      setMobileHeightState(
        maxMemberLines >= 3 ? 'triple' : maxMemberLines >= 2 ? 'double' : 'base'
      );
    };

    updateHeightState();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateHeightState);

    if (memberNameRef.current) {
      resizeObserver?.observe(memberNameRef.current);
    }

    if (memberLocationRef.current) {
      resizeObserver?.observe(memberLocationRef.current);
    }

    window.addEventListener('resize', updateHeightState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateHeightState);
    };
  }, [hasBadgeData, contestantFullName, contestantLocation, showMemberInfo]);

  if (!badgeData) {
    return null;
  }

  const badgeHeading =
    normalizeBadgeText(badgeLabel) ||
    normalizeBadgeText(contestName) ||
    normalizeBadgeUnknownText(badgeLogo?.value?.alt) ||
    'Recipe badge';

  const badgeDescription = uniqueBadgeText([
    normalizeBadgeText(badgeLabel) !== badgeHeading ? normalizeBadgeText(badgeLabel) : undefined,
    showMemberInfo ? normalizeBadgeText(contestantFullName) : undefined,
    showMemberInfo ? normalizeBadgeText(contestantLocation) : undefined,
    normalizeBadgeText(contestName) !== badgeHeading ? normalizeBadgeText(contestName) : undefined,
    normalizeBadgeText(badgeText),
  ]).join('. ');

  const badgeHeadingId = `badge-heading-${accessibilityId}`;
  const badgeDescriptionId = badgeDescription ? `badge-description-${accessibilityId}` : undefined;

  const body = (
    <>
      <div className={styles.visualRail} aria-hidden="true">
        {badgeLogo?.value?.src && (
          <div className={styles.logoShell}>
            <div className={styles.logo}>
              <ImageWrapper field={badgeLogo} editable={false} className={styles.logoImage} />
            </div>
            <img
              alt=""
              aria-hidden="true"
              className={styles.logoRing}
              height="124"
              src="/assets/badge-ring-desktop.svg"
              width="124"
            />
          </div>
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.textBlock}>
          <div className={styles.copyGroup}>
            {showMemberInfo && (contestantFullName || contestantLocation) && (
              <div className={styles.memberInfo}>
                {contestantFullName && (
                  <p ref={memberNameRef} className={styles.memberName}>
                    {contestantFullName}
                  </p>
                )}
                {contestantLocation && (
                  <p ref={memberLocationRef} className={styles.memberLocation}>
                    {contestantLocation}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className={styles.copyGroup}>
            {(badgeLabel || contestName) && (
              <div className={styles.contestDetails}>
                {badgeLabel && <p className={styles.label}>{badgeLabel}</p>}
                {badgeLabel && contestName && (
                  <span className={styles.separator} aria-hidden="true" />
                )}
                {contestName && <div className={styles.title}>{contestName}</div>}
              </div>
            )}
            {badgeText && <p className={styles.text}>{badgeText}</p>}
          </div>
        </div>
      </div>
      {badgeRibbon?.value?.src && (
        <div className={styles.edge} aria-hidden="true">
          <ImageWrapper field={badgeRibbon} editable={false} className={styles.edgeImage} />
        </div>
      )}
    </>
  );

  return (
    <div className={styles.badgeWrapper}>
      <div className={styles.badgePaddingLayer}>
        <section
          className={styles.badgeSection}
          data-component="authorable/shared/content/badge/badge"
          aria-labelledby={badgeHeadingId}
          aria-describedby={badgeDescriptionId}
        >
          <span className={styles.srOnly} id={badgeHeadingId}>
            {badgeHeading}
          </span>
          {badgeDescriptionId && (
            <span className={styles.srOnly} id={badgeDescriptionId}>
              {badgeDescription}
            </span>
          )}
          {badgeLinkURL ? (
            <a
              className={styles.badge}
              href={badgeLinkURL}
              data-mobile-height-state={mobileHeightState}
              aria-labelledby={badgeHeadingId}
              aria-describedby={badgeDescriptionId}
            >
              {body}
            </a>
          ) : (
            <div className={styles.badge} data-mobile-height-state={mobileHeightState}>
              {body}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
